import { runtimeConfig } from "../../lib/runtimeConfig";

/**
 * Client side of the IMAGE additional-field upload flow (Phase 2 of the
 * IMAGE spec). The form widget uploads the file to
 * POST /api/runtime/af-image-upload as soon as it is picked, keeps the
 * returned `pendingId` in form state and submits the field value as
 * `{__pendingUpload: <pendingId>}` — the worker verifies the pending row
 * and promotes the object after the guest write. The stored value is never
 * client-writable.
 *
 * Caps/allowlist mirror the worker's route (5 MB public cap; jpeg/png/
 * webp/gif) so obviously-bad picks fail locally without burning a request.
 */

export const MAX_PUBLIC_AF_IMAGE_BYTES = 5 * 1024 * 1024;

export const ALLOWED_IMAGE_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const IMAGE_FILE_ACCEPT = ALLOWED_IMAGE_CONTENT_TYPES.join(",");

/**
 * Hidden-input marker for an EXPLICIT "remove stored image" action.
 * collectFieldValues maps it to the literal JSON `null` the worker treats
 * as a clear (delete R2 object + release storage). Distinct from `""`,
 * which means "untouched" and is OMITTED from the submission entirely —
 * absent = keep the stored image.
 */
export const IMAGE_CLEAR_SENTINEL = "__CLEAR__";

/**
 * Does a session-prefill value look like a STORED image value
 * (`{key, contentType, size}` set server-side by the finalize step)?
 * Drives the "Bild gespeichert" chip + the required-check pass on
 * data-input forms.
 */
export function isStoredImageValue(value: unknown): boolean {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const key = (value as Record<string, unknown>).key;
  return typeof key === "string" && key.startsWith("af-images/");
}

export type ImageFileError = "wrongType" | "tooBig" | null;

/** Pre-upload client check. Returns the error kind or null when ok. */
export function validateImageFile(file: {
  type: string;
  size: number;
}): ImageFileError {
  if (
    !(ALLOWED_IMAGE_CONTENT_TYPES as readonly string[]).includes(file.type)
  ) {
    return "wrongType";
  }
  if (file.size <= 0 || file.size > MAX_PUBLIC_AF_IMAGE_BYTES) {
    return "tooBig";
  }
  return null;
}

/**
 * Upload the picked file. Plain fetch (not apiFetch): multipart bodies must
 * NOT carry a manual Content-Type header — the browser sets the boundary.
 * Throws on any non-2xx / malformed response; the widget maps that to the
 * generic `imageUploadFailed` message.
 */
export async function uploadAfImage(
  file: Blob,
  fieldName: string,
): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  form.append("fieldName", fieldName);
  const response = await fetch(`${runtimeConfig.apiBase}/af-image-upload`, {
    method: "POST",
    body: form,
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`af-image-upload failed: ${response.status}`);
  }
  const payload = (await response.json()) as { pendingId?: unknown };
  if (typeof payload.pendingId !== "string" || payload.pendingId.length === 0) {
    throw new Error("af-image-upload: malformed response");
  }
  return payload.pendingId;
}
